import { useMutation } from "@tanstack/react-query";
import axiosInstance from "../config/axiosConfig";
import useConfigStore from "../store/configStore";

export interface ChatMessagePayload {
  user_id: string;
  question: string;
}

export interface ChatMessageResponse {
  success: boolean;
  message: string;
  data: {
    userMessage: string;
    aiResponse: {
      response: string;
    };
  };
}

export const sendChatMessage = async (
  payload: ChatMessagePayload
): Promise<ChatMessageResponse> => {
  const endpoint = useConfigStore.getState().getEndpoint('message');
  const response = await axiosInstance.post(endpoint, payload);
  return response.data;
};

export const useSendChatMessage = () => {
  return useMutation<ChatMessageResponse, Error, ChatMessagePayload>({
    mutationFn: sendChatMessage,
  });
};

export async function cachedImagesOfAIBot(payload: { timestamp: string }) {
  const endpoint = useConfigStore.getState().getEndpoint('cachedImages');
  const { data } = await axiosInstance.post(endpoint, payload);
  return data.data;
}

export interface ChatHistoryMessage {
  id: string;
  sender: "user" | "ai";
  project_id: string;
  name: string;
  content: string;
  time: string;
}

export interface ChatHistoryResponse {
  status: string;
  message: string;
  data: {
    messages: ChatHistoryMessage[];
    message: string;
  };
}

export const fetchChatHistory = async (
  limit?: number
): Promise<ChatHistoryResponse> => {
  const config = useConfigStore.getState().getConfig();
  const defaultLimit = config?.defaultLimit || 10;
  const actualLimit = limit || defaultLimit;
  const endpoint = useConfigStore.getState().getEndpoint('fetchHistory');
  const response = await axiosInstance.get(`${endpoint}/${actualLimit}`);
  return response.data;
};

export const useFetchChatHistory = () => {
  return useMutation<ChatHistoryResponse, Error, number>({
    mutationFn: fetchChatHistory,
  });
};

// Fetch chat sessions by project name
export interface ChatSession {
  _id: string;
  session_title: string;
  created_at: string;
  user_id: string;
  project_id: string;
  id: string;
  project_name: string;
  last_message?: string;
  timestamp?: string;
  last_message_id?: string;
  index_id: number;
  total_sessions_count: number;
  updated_at?: string | null;
  course_id?: number | string;
}

export interface FetchChatSessionsResponse {
  status: string;
  message: string;
  data: ChatSession[];
}

export const fetchChatSessionsByProjectName = async (
  projectName: string
): Promise<FetchChatSessionsResponse> => {
  const endpoint = useConfigStore.getState().getEndpoint('fetchSessions');
  const response = await axiosInstance.get(endpoint, {
    params: { search: projectName },
  });
  return response.data;
};

export const useFetchChatSessionsByProjectName = () => {
  return useMutation<FetchChatSessionsResponse, Error, string>({
    mutationFn: fetchChatSessionsByProjectName,
  });
};

// Fetch chat history by session index - Updated for new API structure
export interface ChatHistorySession {
  session_id: number;
  chat_id: number;
  message: string;
  response: string;
  action: string;
  chat_type: string;
  agent_type: string;
  reaction_type: string | null;
  created_at: string;
  replied_to: number | null;
  replied_message: string | null;
}

// New interface for session history response structure
export interface SessionHistoryItem {
  session_id: number;
  session_topic: string;
  session_created_at: string;
  session_count: number;
  chat_id: number;
  last_message: string;
  chat_type: string;
  timestamp: string;
  course_id?: number | string;
}

export interface FetchChatHistorySessionResponse {
  session_chats: ChatHistorySession[];
}

export const fetchChatHistoryBySessionIndex = async (
  index_id?: number,
  action?: "up" | "down"
): Promise<FetchChatHistorySessionResponse> => {
  const params: any = {};
  if (index_id !== undefined) params.session_id = index_id;
  if (action) params.chat_order_action = action;
  const endpoint = useConfigStore.getState().getEndpoint('chatHistorySession');
  const response = await axiosInstance.get(endpoint, { params });
  return response.data;
};

export const useFetchChatHistoryBySessionIndex = () => {
  return useMutation<
    FetchChatHistorySessionResponse,
    Error,
    { index_id?: number | undefined; action?: "up" | "down" | undefined }
  >({
    mutationFn: ({ index_id, action }) =>
      fetchChatHistoryBySessionIndex(index_id, action),
  });
};

// Chat history navigation API
export interface ChatHistoryNavigationPayload {
  entity?: string;
  session_id: number;
  chat_order_action?: string; // e.g., 'up' or 'down'
  session_index?: number; // Current session index
}

export interface ChatHistoryNavigationResponse {
  success: boolean;
  message: string;
  session_chats: ChatHistorySession[];
}

export const chatHistoryNavigation = async (
 payload: ChatHistoryNavigationPayload
): Promise<ChatHistoryNavigationResponse> => {
  const endpoint = useConfigStore.getState().getEndpoint('chatHistoryNavigation');
  const { data } = await axiosInstance.get(endpoint, { params: payload});
  return data;
};

export const useChatHistoryNavigation = () => {
  return useMutation<
    ChatHistoryNavigationResponse,
    Error,
    ChatHistoryNavigationPayload
  >({ mutationFn: chatHistoryNavigation });
};

export async function addFeedbackToMessage(payload: {
  chat_id: number
  reaction_type: string
  reaction_description?: string
}) {
  const endpoint = useConfigStore.getState().getEndpoint('handleVote');
  const response = await axiosInstance.post(endpoint, payload);
  return response;
}
